import { useRouter } from 'next/router';
import { Text, Flex, Stack } from '@pairy/pds-react';
import { useLocalStorage } from '@pairy/react-hooks';
import { useEffect, useState } from 'react';

import SortTypeDropdown from '@/components/User/_components/common/SortTypeDropdown';
import useFileBulkClientTrackList, {
  SortType,
} from '@/hooks/queries/file/useFileBulkClientTrackList';
import { localStorageKey } from '@/constants/localStorageKey';
import VersionDropdown from '@/components/User/_components/pdf/VersionDropdown';
import PaywalledBlur from '@/components/User/_components/pdf/PaywalledBlur';
import ProPlanUpgradeModal from '@/components/User/_components/common/ProPlanUpgradeModal';
import BlurredCompactItem from '@/components/User/_components/common/BlurredCompactItem';

import Box from './Box';

const items = [
  { label: '최근 열람 시간 순', value: 'recent' },
  { label: '자주 열람한 순', value: 'most' },
] as const;

const Bulk = () => {
  const router = useRouter();
  const fileId = Number(router.query.fileId);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const [sortType, setSortType] = useLocalStorage<SortType>(
    localStorageKey.fileBulkDropdown,
    'recent',
  );

  const [selectedFileVersionId, setSelectedFileVersionId] = useState(fileId);

  useEffect(() => {
    setSelectedFileVersionId(fileId);
  }, [fileId]);

  const { data } = useFileBulkClientTrackList(
    {
      fileId: selectedFileVersionId,
      sort: sortType ?? 'recent',
    },
    {
      enabled: Boolean(selectedFileVersionId),
      select: (data) => data.data.data.clients,
    },
  );

  const handleFileVersionChange = (id: number) => {
    setSelectedFileVersionId(id);
  };

  const firstBlurredIndex = (data ?? []).findIndex(
    (client) => client.isBlurred,
  );

  return (
    <Stack direction="column" gap={16}>
      <Flex
        display="inline-flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack gap={4} alignItems="center">
          <Stack gap={4} width="fit-content" alignItems="center">
            <VersionDropdown
              originalFileId={fileId}
              selectedFileVersionId={selectedFileVersionId}
              handleFileVersionChange={handleFileVersionChange}
            />
            <Text as="p" variant="xl-semibold">
              열어본 고객
            </Text>
          </Stack>
          <Text variant="md-medium" color="zinc_400">
            {`총 ${data?.length ?? ''}곳`}
          </Text>
        </Stack>
        <SortTypeDropdown
          initialValue={sortType}
          items={items}
          onValueChanges={(value) => setSortType(value)}
        />
      </Flex>
      {data?.length === 0 ? (
        <Flex direction="column" alignItems="center" marginTop={32}>
          <Text
            variant="md-medium"
            color="zinc_400"
            textAlign="center"
            marginTop={8}
          >
            좌측에서 플랫폼을 선택해
            <br />
            반응 추적용 링크를 복사한 뒤<br />
            고객에게 보내는 메시지에 삽입하세요.
          </Text>
        </Flex>
      ) : (
        <Stack
          direction="column"
          gap={8}
          className={'pdf-tracking-client-list'}
        >
          {data?.map((client, index) =>
            client.isBlurred ? (
              index === firstBlurredIndex ? (
                <PaywalledBlur
                  key={client.email ?? index}
                  title="최근 7일 데이터만 확인할 수 있어요"
                  description="업그레이드하면 전체 기간 데이터를 확인할 수 있어요."
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                >
                  <Box {...client} />
                </PaywalledBlur>
              ) : (
                <BlurredCompactItem
                  key={client.email ?? index}
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                >
                  <Box {...client} />
                </BlurredCompactItem>
              )
            ) : (
              <Box key={client.email} {...client} />
            ),
          )}
        </Stack>
      )}
      {isUpgradeModalOpen && (
        <ProPlanUpgradeModal onOpenChange={setIsUpgradeModalOpen} />
      )}
    </Stack>
  );
};

export default Bulk;
